import React from 'react'

import { useAutomationConnections } from 'data/hooks/automations/useAutomationConnections'
import { useAutomationIntegrations } from 'data/hooks/automations/useAutomationIntegrations'

import { Box } from 'ui/components/Box'
import { Dropdown, DropdownButton, DropdownContent, DropdownItem } from 'ui/components/Dropdown'
import { DropdownFooterItem } from 'ui/components/Dropdown/DropdownFooterItem'

import { ConnectionAuthor } from './ConnectionAuthor'
import { useAddNewConnectionModal } from './useAddNewConnectionModal'

type ConnectionDropdownProps = {
    // value is expected to be the nango connection id of the automaton connection
    value?: string
    onChange: (connection: AutomationConnectionDto) => void
    providerId: AutomationIntegrationId
}

export const ConnectionDropdown: React.FC<ConnectionDropdownProps> = ({
    value,
    onChange,
    providerId,
}) => {
    const { data: connections, isLoading: isLoadingConnections } = useAutomationConnections()
    const { data: automationIntegrationData, isLoading: isLoadingIntegrations } =
        useAutomationIntegrations()

    const supportedConnections = connections?.filter((c) => c.integration_id === providerId)
    const selectedConnection = supportedConnections?.find((c) => c.nango_connection_id === value)
    const selectedIntegration = automationIntegrationData?.integrations.find(
        (p) => p.id === selectedConnection?.integration_id
    )

    const onAddNewConnection = (connection: AutomationConnectionDto) => {
        onChange(connection)
    }
    const addNewConnection = useAddNewConnectionModal({
        integrationId: providerId,
        onSuccessfulAuth: onAddNewConnection,
    })

    return (
        <Dropdown>
            <DropdownButton
                isLoading={isLoadingConnections || isLoadingIntegrations}
                variant="secondary"
                size="m"
                style={{ justifyContent: 'flex-start' }}
            >
                {selectedConnection ? (
                    <ConnectionDisplay
                        connection={selectedConnection}
                        integration={selectedIntegration}
                    />
                ) : (
                    <>
                        <Box
                            width="xl"
                            height="xl"
                            background="surfaceStronger"
                            noShrink
                            mr="m"
                            rounded="xs"
                        />
                        Pick a connection
                    </>
                )}
            </DropdownButton>
            <DropdownContent
                align="end"
                footer={
                    <DropdownFooterItem
                        key="new"
                        onClick={addNewConnection}
                        startIcon={{ name: 'Plus' }}
                        variant="action"
                        label="Add new connection"
                    />
                }
            >
                {supportedConnections?.map((connection) => {
                    const integration = automationIntegrationData?.integrations?.find(
                        (integration) => integration.id === connection.integration_id
                    )
                    if (!integration) return null

                    return (
                        <DropdownItem
                            key={connection.nango_connection_id}
                            label={
                                <ConnectionDisplay
                                    connection={connection}
                                    integration={integration}
                                />
                            }
                            multiSelect
                            checked={
                                connection.nango_connection_id ===
                                selectedConnection?.nango_connection_id
                            }
                            onCheckedChange={(checked) => {
                                if (checked) onChange(connection)
                            }}
                            closeOnSelect={true}
                        />
                    )
                })}
            </DropdownContent>
        </Dropdown>
    )
}

type ConnectionDisplayProps = {
    connection: AutomationConnectionDto
    integration?: AutomationIntegrationDto
}

const ConnectionDisplay: React.FC<ConnectionDisplayProps> = ({ connection, integration }) => {
    if (!integration) return null

    return (
        <Box
            fontWeight="bodyRegular"
            flex
            center
            gap="xs"
            py={!connection.created_by_user_sid ? 's' : undefined}
        >
            <Box
                rounded="xs"
                style={{
                    height: '35px',
                    width: '35px',
                    padding: '8px',
                }}
            >
                <img src={integration?.image_url} alt={`${integration?.name} logo`} />
            </Box>
            {integration.name}
            {connection.created_by_user_sid && (
                <>
                    <Box as="span">by</Box>
                    <ConnectionAuthor userId={connection.created_by_user_sid} fontSize="bodyM" />
                </>
            )}
        </Box>
    )
}
